/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import * as authSelectors from "../../auth/authSelectors";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Dropdown, MenuProps } from "antd";
import React, { useState } from "react";
import { DropdownMenuWrapper } from "../../common/DropdownMenuWrapper";
import { Divider } from "../../../components/Divider";
import { colors } from "../../../components/colors";
import { DropdownItemLink } from "../../common/DropdownItemLink";
import { Menu } from "../models/Menus";
import { DeleteMenuSelector } from "./DeleteMenuSelector";
import { ResetDraftSelector } from "./ResetDraftSelector";
import { PushMenuToSalesChannelsSelector } from "./PushMenuToSalesChannelsSelector";
import {
  useCreateMenu,
  usePublishMenuDraft,
  useUpdateMenuDraftProducts,
} from "../service/menus-api";
import { useSelector } from "react-redux";
import { DropdownItemButton } from "../../common/DropdownItemButton";
import { menuHttp } from "../service/menus-http";
import { useNavigate } from "react-router-dom";
import { toastr } from "react-redux-toastr";
import { routes } from "../../../appRoutingHelpers";
import { ActionSpinner } from "../../../components/ActionSpinner";
import { LoadingModal } from "../../../components/LoadingModal";
import { DuplicateMenuAndProductsSelector } from "./DuplicateMenuAndProductsSelector";
import { getErrorMessage } from "../../../error-handler";

type Props = {
  menuItem: Menu | undefined;
  editLink: string;
};

export const ellipsisStyle = css`
  display: flex;
  width: 32px;
  height: 32px;
  min-width: 32px;
  min-height: 32px;
  cursor: pointer;
  align-items: center;
  justify-content: center;
  color: ${colors.textSecondary};
  &:hover {
    color: ${colors.textPrimary};
  }
`;

export const dropdownSyncTextWrapper = css`
  display: flex;
  flex-direction: column;
  padding: 0px 8px 8px 8px;
`;

export const MenuDropdown = ({ menuItem, editLink }: Props) => {
  const organizationKeyName = useSelector(
    authSelectors.selectedOrganizationKeyName
  );
  const navigate = useNavigate();
  const [open, setOpen] = useState<boolean>(false);
  const [isLoadingDuplication, setIsLoadingDuplication] = useState<boolean>(
    false
  );

  const handleClick = () => {
    setOpen(false);
  };

  const { mutateAsync: updateMenuDraftProducts } = useUpdateMenuDraftProducts();
  const { mutateAsync: createMenu } = useCreateMenu();
  const { mutateAsync: publishMenu } = usePublishMenuDraft();

  const duplicateMenu = async () => {
    setIsLoadingDuplication(true);
    if (!menuItem) {
      return;
    }
    try {
      // Create an identical menu
      const id = await createMenu({
        data: {
          countryCode: menuItem.countryAlpha2Code,
          internalNote: menuItem?.internalNote || "",
          name: `Duplicate of ${menuItem.name}`,
          isMenuOverwritesEnabled: false,
          currency: menuItem.currency,
        },
        organizationKeyName,
      });

      // Fetch the current active products for the menu
      const menuProducts = await menuHttp.getMenuProducts(
        organizationKeyName,
        menuItem.id
      );

      // Update menu and save as draft
      await updateMenuDraftProducts({
        data: {
          products: menuProducts.map((p) => ({
            id: p.id,
            sortKey: p.sortKey,
          })),
        },
        id,
        organizationKeyName,
      });

      // Publish it
      await publishMenu({
        id,
        organizationKeyName,
      });
      navigate(routes.menuEdit(organizationKeyName, id));
      toastr.success("Success", "Menu successfully duplicated");
    } catch (error: unknown) {
      const message = getErrorMessage(error);
      toastr.error("Error", message);
    } finally {
      setIsLoadingDuplication(false);
    }
  };

  const items: MenuProps["items"] = [
    {
      key: "1",
      label: (
        <DropdownItemLink
          to={editLink}
          icon={<FontAwesomeIcon icon={solid("pen")} />}
        >
          Edit
        </DropdownItemLink>
      ),
    },
    {
      key: "2",
      label: (
        <DropdownItemButton
          onClick={duplicateMenu}
          icon={<FontAwesomeIcon icon={solid("clone")} />}
        >
          {isLoadingDuplication ? <ActionSpinner /> : "Duplicate menu"}
        </DropdownItemButton>
      ),
    },
    {
      key: "3",
      label: <DuplicateMenuAndProductsSelector menuItem={menuItem} />,
    },
    {
      key: "4",
      label: (
        <PushMenuToSalesChannelsSelector
          id={menuItem?.id}
          buttonType="dropdown"
          onClick={handleClick}
        />
      ),
    },
    ...(menuItem?.haveChanges
      ? [
          {
            key: "5",
            label: (
              <ResetDraftSelector
                id={menuItem.id!}
                buttonType="dropdown"
                onClick={handleClick}
              />
            ),
          },
        ]
      : []),
  ];

  const dropdownInnerWrapper = css`
    display: flex;
    flex-direction: column;
    padding-top: 8px;
    padding-left: 8px;
    padding-right: 8px;
    ${menuItem?.salesChannelsCount !== 0 &&
    `
  padding-bottom: 8px;
  `}
  `;

  return (
    <>
      <LoadingModal
        header={"Duplicating menu..."}
        isOpen={isLoadingDuplication}
      />
      <Dropdown
        menu={{
          items: items,
        }}
        open={open}
        onOpenChange={setOpen}
        placement="bottomRight"
        dropdownRender={(menu) => (
          <DropdownMenuWrapper>
            <div css={dropdownInnerWrapper}>
              {React.cloneElement(menu as React.ReactElement, {
                style: {
                  display: "flex",
                  flexDirection: "column",
                  gap: "4px",
                  boxShadow: "none",
                  padding: "0px",
                  backgroundColor: "transparent",
                },
              })}
            </div>
            {menuItem?.salesChannelsCount === 0 && menuItem.id && (
              <>
                <Divider />
                <DeleteMenuSelector
                  onClick={handleClick}
                  menuId={menuItem.id}
                />
              </>
            )}
          </DropdownMenuWrapper>
        )}
      >
        <div css={ellipsisStyle}>
          <FontAwesomeIcon icon={solid("ellipsis")} />
        </div>
      </Dropdown>
    </>
  );
};
